import React, { useState } from "react";
import {
  Box,
  Button,
  TextInput,
  Heading,
  Layer,
  Text,
  Select,
  CheckBox,
} from "grommet";
import { Edit } from "grommet-icons";
import { useFetchHotel, useFetchRoomTypes } from "./redux/hooks";
import { useTranslation } from "react-i18next";

export default function EditDoor(props) {
  const { t } = useTranslation();
  const { type, onSubmit, onBulkSubmit, doorsToEdit, disabled, allDoors } =
    props;
  const { roomTypes } = useFetchRoomTypes();
  const { hotel } = useFetchHotel();
  const [door, setDoor] = useState();
  const [open, setOpen] = useState();
  const [restrictedToDoors, setRestrictedToDoors] = useState();
  const isBulkEdit = doorsToEdit?.length > 1;

  const onOpen = () => {
    const firstDoor = doorsToEdit?.[0];
    if (firstDoor) {
      setDoor(firstDoor);
      const nextRestrictedToDoors = (firstDoor.restrictedToDoorIds || [])
        .map((id) => allDoors.find((d) => `${d._id}` === id)?.name)
        .join(",");
      setRestrictedToDoors(nextRestrictedToDoors);
      setOpen(true);
    }
  };
  const onClose = () => setOpen(undefined);

  function handleInputChange({ target: { name, value } }) {
    setDoor({ ...door, [name]: value });
  }
  function handleCheckboxChange({ target: { name, checked } }) {
    setDoor({ ...door, [name]: checked });
  }
  function handleRestrictedToDoorsChange({ target: { name, value } }) {
    setRestrictedToDoors(value);
    const restrictedToDoorIds = (value || "")
      .split(",")
      .map((name) => allDoors.find((d) => d.name === name.trim())?._id)
      .filter(Boolean);
    setDoor({ ...door, restrictedToDoorIds });
  }
  const handleEdit = () => {
    onClose();
    if (hotel.serviceType === "messaging") {
      if (isBulkEdit) onBulkSubmit({ doorIds: doorsToEdit.map(d => d._id), update: { roomType: door.roomType?._id } });
      else onSubmit({ _id: door._id, name: door.name, pmsId: door.pmsId, roomType: door.roomType?._id });
      return;
    }
    if (isBulkEdit) {
      onBulkSubmit({
        doorIds: doorsToEdit.map(({ _id }) => _id),
        update: {
          roomType: door.roomType?._id,
          validNoLock: Boolean(door.validNoLock),
          allowConcurrentBookings: Boolean(door.allowConcurrentBookings),
        },
      });
      return;
    }
    onSubmit({
      ...door,
      ...(door.type === "room" && { roomType: door.roomType?._id }),
      ...(hotel.lockType === "oracode" && door.lockName?.length > 0
        ? { lockId: 1 }
        : { lockId: undefined }),
    });
  };

  return (
    <div>
      <Button
        primary
        icon={<Edit size="small" />}
        size="small"
        color="neutral-3"
        onClick={onOpen}
        disabled={disabled}
      />
      {open && (
        <Layer position="center" onClickOutside={onClose} onEsc={onClose}>
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              {isBulkEdit
                ? t("doorsDialog.editManyTitle")
                : t("doorsDialog.editTitle", { type })}
            </Heading>
            {!isBulkEdit && (
              <>
                <Text>{t("doorsDialog.name")}</Text>
                <TextInput
                  name="name"
                  value={door.name}
                  onChange={handleInputChange}
                />
                {hotel.serviceType !== "messaging" && hotel.lockType === "oracode" && (
                  <>
                    <Text>{t("doorsDialog.oracodeDoorId")}</Text>
                    <TextInput
                      name="lockName"
                      value={door.lockName || ""}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                <Text>{t("doorsDialog.pmsId")}</Text>
                <TextInput
                  name="pmsId"
                  value={door.pmsId || ""}
                  onChange={handleInputChange}
                />
              </>
            )}
            {type === "room" && (
              <>
                <Text>{t("doorsDialog.roomType")}</Text>
                <Select
                  name="roomType"
                  placeholder={t("doorsDialog.selectRoomType")}
                  value={door.roomType?._id}
                  labelKey="name"
                  valueKey={{ key: "_id", reduce: true }}
                  options={roomTypes}
                  onChange={({ value }) =>
                    setDoor({
                      ...door,
                      roomType: roomTypes.find((x) => x._id === value),
                    })
                  }
                />
                {hotel.serviceType !== "messaging" && <Box margin={{ top: "small" }}>
                  <CheckBox
                    name="validNoLock"
                    checked={door.validNoLock}
                    onChange={handleCheckboxChange}
                    label={t("doorsDialog.validWithoutLock")}
                  />
                </Box>}
                {hotel.serviceType !== "messaging" && <Box margin={{ top: "small" }}>
                  <CheckBox
                    name="allowConcurrentBookings"
                    checked={door.allowConcurrentBookings || false}
                    onChange={handleCheckboxChange}
                    label={t("doorsDialog.allowConcurrentBookings")}
                  />
                </Box>}
              </>
            )}
            {type === "area" && (
              <>
                {" "}
                <Text>{t("doorsDialog.restrictedToDoors")}</Text>
                <TextInput
                  name="restrictedToDoorIds"
                  value={restrictedToDoors || ""}
                  onChange={handleRestrictedToDoorsChange}
                />
              </>
            )}
            <Box
              as="footer"
              gap="small"
              direction="row"
              align="center"
              justify="end"
              pad={{ top: "medium", bottom: "small" }}
            >
              <Button
                label={t("common.cancel")}
                onClick={onClose}
                color="dark-3"
              />
              <Button
                label={t("common.edit")}
                onClick={handleEdit}
                primary
                color="status-ok"
              />
            </Box>
          </Box>
        </Layer>
      )}
    </div>
  );
}
